import type { VlanAddressing, VlanMode } from './onu-config.js';
import type { Ipv6AddressMode, Ipv6PrefixMode, ObservedWanIpv6, WanIpv6 } from './wan-ipv6.js';

/**
 * Where each step of the WAN dialog OPENS — the pure defaults behind
 * `apps/web/src/pages/device/WanModeDialog.tsx`, and since 2026-09-14 behind
 * the bulk `wan_mode` action as well.
 *
 * Operator, 2026-09-13, with screenshots of the one-screen dialog: "leave as
 * is should not be an option, also selectable options, the default should be
 * what is already selected if it exists". So every choice is a real option
 * pre-selected to the current value where one is known, and the "Leave as it
 * is" firewall option and the "Leave as the CPE has it" IPv6 tile are gone.
 * What replaces them is not a hidden default: each step opens on the value
 * the row / the CPE / the platform already holds, and the Review step lists
 * exactly what is sent.
 *
 * MOVED here from `apps/web/src/lib/wan-wizard.ts` when the bulk action
 * arrived, because "opens on the current value" is a rule about ONE ONU and a
 * bulk run has N of them: the resolver has to answer, per ONU, exactly what
 * the dialog would have opened on — the row's addressing, the record → CPE →
 * firmware-default IPv6, the CPE → platform → assumed firewall — and two
 * copies of that order are two orders. The web file re-exports these and keeps
 * only the step list (`wanWizardSteps`), which is UI.
 *
 * Nothing here judges a vendor word itself: the IPv6 matrix is the shared
 * `ipv6Refusals` (wan-ipv6.ts), handed in as a function so the same sentences
 * the server refuses with decide which radio is greyed and where a default may
 * land. A default NEVER lands on a refused mode — a row recorded DHCPv6
 * before its CPE turned out to be an F670L V7.1 opens on Auto, with the
 * record's own refusal sentence beside the greyed radio.
 */

/**
 * Where the Addressing step opens: the row's own addressing, else DHCP — on
 * EITHER chassis. The first cut of the wizard opened a Huawei-chassis row
 * recorded PPPoE on DHCP (the PPPoE tile is disabled there, and a held Next
 * behind a tile nobody can press looked worse), and that was the one step
 * where the default was NOT "what is already selected": a pass-through Apply
 * made to record the IPv6 intent — the one thing the dialog does on a Huawei
 * chassis — re-recorded the subscriber's PPPoE as DHCP, and that record is
 * what the first-link push delivers to the CPE (`recordedWanIntent`). Such
 * rows are real: the SmartOLT-compat `set_onu_wan_mode_pppoe` records them
 * on either vendor. So the step opens on the row's own value, disabled tile
 * or not; `addressingReady` below is what lets Next pass it unchanged.
 */
export function defaultAddressing(input: {
  recorded: VlanAddressing | null | undefined;
}): VlanAddressing {
  return input.recorded ?? 'dhcp';
}

/**
 * Whether Next may leave the Addressing step.
 *
 *   fieldsReady  the IPv4 fields validate for the addressing chosen — DHCP
 *                always; PPPoE a username AND a password; static the four
 *                addresses.
 *   unchanged    the addressing is the row's own and no particular was typed.
 *
 * On a ZTE chassis the fields must validate every time: the apply re-renders
 * the `wan-ip` line and a PPPoE line needs the password, which is stored
 * encrypted and never echoed back, so it is typed again even to re-apply the
 * same account (as it always was). On a Huawei chassis no `wan-ip` line is
 * rendered — the addressing is a RECORD delivered to the CPE at its next link
 * — so an unchanged row passes as it is, PPPoE password untyped, and the
 * PATCH leaves the row's particulars (its encrypted password included) alone
 * (`addressingPatch`). A changed addressing, or a particular typed, validates
 * on either chassis: a recorded intent with half an account is no intent.
 */
export function addressingReady(input: {
  zte: boolean;
  addressing: VlanAddressing;
  /** The row's own addressing when it is routed; null/undefined for a bridged or absent row. */
  recorded: VlanAddressing | null | undefined;
  /** Whether the operator typed any particular (a PPPoE username or password, a static field). */
  particularsTyped: boolean;
  fieldsReady: boolean;
}): boolean {
  const unchanged =
    input.recorded != null && input.addressing === input.recorded && !input.particularsTyped;
  if (!input.zte && unchanged) return true;
  return input.fieldsReady;
}

/**
 * What of the addressing rides the row PATCH: the addressing and its
 * particulars when the operator changed the addressing or typed a particular,
 * NOTHING of it when the row is re-applied as it is. The PATCH route treats an
 * empty `pppoePassword` as "clear it" (`pppoePasswordEnc` → null), so a PATCH
 * that re-sent `addressing: 'pppoe'` with the untyped password would wipe the
 * stored one; sending neither keeps the row's own. The `mode` and the IPv6
 * record ride the PATCH regardless — they are the dialog's own questions.
 */
export function addressingPatch<P extends Record<string, unknown>>(input: {
  addressing: VlanAddressing;
  recorded: VlanAddressing | null | undefined;
  particularsTyped: boolean;
  particulars: P;
}): ({ addressing: VlanAddressing } & P) | Record<string, never> {
  const unchanged =
    input.recorded != null && input.addressing === input.recorded && !input.particularsTyped;
  return unchanged ? {} : { addressing: input.addressing, ...input.particulars };
}

export const IPV6_ADDRESS_MODES: readonly Ipv6AddressMode[] = ['dhcpv6', 'auto', 'static', 'none'];
export const IPV6_PREFIX_MODES: readonly Ipv6PrefixMode[] = ['dhcpv6_pd', 'static', 'none'];

/**
 * The order the platform tries when NOTHING is recorded or observed — the
 * "firmware default is dual stack" row of the IPv6 matrix, with the address
 * mode the first of Auto → DHCPv6 → None the target allows and the prefix
 * DHCPv6-PD if allowed, else None.
 *
 * Auto FIRST, because it is what the fleet's firmware default actually reads:
 * 24/27 F670L V7.1 read Both / IPv6AutoModeEnable=true / DHCP and 105 Huawei
 * INTERNET connections read AutoConfigured / PrefixDelegation (NBI,
 * 2026-09-12), and Auto is the one address mode no dialect refuses. The first
 * cut put DHCPv6 first, and that mattered exactly where the order decides —
 * a target the API resolves as `tr069 / unknown` (a CPE not linked yet, or a
 * linked one while the ACS is briefly away), which refuses nothing — so with
 * "leave as the CPE has it" gone, every routed pass-through Apply on such a
 * unit recorded DHCPv6 + DHCPv6-PD, which the V7.1 and Huawei dialects then
 * refused at first link (`fix_failed` on every migrated subscriber), or, with
 * the ACS back by the time omci-wan re-gated, 400'd the whole WAN apply. Only
 * the register V9 unit takes DHCPv6, and Auto + DHCPv6-PD is a verified cell
 * there too.
 */
export const IPV6_ADDRESS_DEFAULT_ORDER: readonly Ipv6AddressMode[] = ['auto', 'dhcpv6', 'none'];
export const IPV6_PREFIX_DEFAULT_ORDER: readonly Ipv6PrefixMode[] = ['dhcpv6_pd', 'none'];

/**
 * The matrix read PER RADIO: which sentences an address mode gets whatever
 * prefix it is paired with, and the other way round — what greys a radio
 * and what a default must avoid.
 *
 * `ipv6Refusals` judges a whole intent, and its dual-stack sentences are
 * independent — a static address is refused beside any prefix, prefix None
 * on a Huawei beside any address — so a mode's own sentences are the ones
 * present for EVERY companion. The intersection is taken rather than judging
 * against one "known good" companion, because "Auto is never refused" is a
 * fact about today's matrix, not a contract; the day a cell changes, this
 * still attributes each sentence to the radio it is about. `wholesale` is
 * what IPv4 only gets: the least an intent can ask, refused only where the
 * target takes no IPv6 choice at all.
 */
export interface Ipv6ChoiceMatrix {
  wholesale: string[];
  address: Record<Ipv6AddressMode, string[]>;
  prefix: Record<Ipv6PrefixMode, string[]>;
}

export function ipv6ChoiceMatrix(refusals: (intent: WanIpv6) => string[]): Ipv6ChoiceMatrix {
  const dual = (addressMode: Ipv6AddressMode, prefixMode: Ipv6PrefixMode): string[] =>
    refusals({ protocol: 'dual_stack', addressMode, prefixMode });
  const common = (lists: string[][]): string[] =>
    lists.length === 0 ? [] : lists[0]!.filter((s) => lists.every((l) => l.includes(s)));
  const address = Object.fromEntries(
    IPV6_ADDRESS_MODES.map((a) => [a, common(IPV6_PREFIX_MODES.map((p) => dual(a, p)))]),
  ) as Record<Ipv6AddressMode, string[]>;
  const prefix = Object.fromEntries(
    IPV6_PREFIX_MODES.map((p) => [p, common(IPV6_ADDRESS_MODES.map((a) => dual(a, p)))]),
  ) as Record<Ipv6PrefixMode, string[]>;
  return { wholesale: refusals({ protocol: 'ipv4' }), address, prefix };
}

/**
 * Where the IP protocol step opens — record → observed → firmware default,
 * and never on a refused mode.
 *
 *   record    the row's own `ipv6` (`attachedRow.ipv6`), when it has one
 *   observed  what the CPE reads now (`GET …/wan-ipv6` `observed`): dual
 *             stack when it reads dual_stack, IPv4 when it reads ipv4
 *   default   dual stack — what the matrix says every family ships as
 *             (24/27 F670L V7.1 and 105 Huawei INTERNET connections read
 *             dual by firmware default, NBI 2026-09-12)
 *
 * The protocol follows that order; the two modes follow it independently,
 * each falling through record → observed → `IPV6_*_DEFAULT_ORDER` to the
 * first mode the target allows. The one shape the schema itself refuses,
 * address None beside prefix None, is IPv4 wearing another name: when the
 * record/observed words land there (a V9 unit reading IPMode=Both with all
 * three enables false does) the platform's own order is used instead, and
 * IPv4 only is the last resort. Null when the target refuses every intent —
 * the step is absent then (`wanWizardSteps`), so nothing opens on nothing.
 */
export function defaultIpv6Intent(input: {
  record: WanIpv6 | null | undefined;
  observed: ObservedWanIpv6 | null | undefined;
  refusals: (intent: WanIpv6) => string[];
}): WanIpv6 | null {
  const matrix = ipv6ChoiceMatrix(input.refusals);
  if (matrix.wholesale.length > 0) return null;
  const { record, observed } = input;

  const protocol: WanIpv6['protocol'] =
    record?.protocol ??
    (observed?.protocol === 'ipv4' || observed?.protocol === 'dual_stack'
      ? observed.protocol
      : 'dual_stack');
  if (protocol === 'ipv4') return { protocol: 'ipv4' };
  const modes = defaultIpv6Modes({ ...input, matrix });
  return modes ? { protocol: 'dual_stack', ...modes } : { protocol: 'ipv4' };
}

/**
 * The two dual-stack modes on their own — where the "IPv6 address" and
 * "IPv6 prefix delegation" rows open, whichever protocol tile is chosen. The
 * dialog needs them apart from the protocol: a unit that opens on IPv4 only
 * (the register V9 unit as it was found) and is switched to dual stack by
 * the operator must still open its rows on the record / the CPE's words /
 * the platform order, not on the first radio. Null when no allowed pair
 * exists (every intent refused, or only None beside None).
 */
export function defaultIpv6Modes(input: {
  record: WanIpv6 | null | undefined;
  observed: ObservedWanIpv6 | null | undefined;
  refusals: (intent: WanIpv6) => string[];
  /** The matrix when the caller already has it, so it is not derived twice. */
  matrix?: Ipv6ChoiceMatrix;
}): { addressMode: Ipv6AddressMode; prefixMode: Ipv6PrefixMode } | null {
  const matrix = input.matrix ?? ipv6ChoiceMatrix(input.refusals);
  if (matrix.wholesale.length > 0) return null;
  const { record, observed } = input;
  const seen = {
    address: [
      ...(record?.protocol === 'dual_stack' ? [record.addressMode] : []),
      ...(observed?.protocol === 'dual_stack' && observed.addressMode !== 'unknown'
        ? [observed.addressMode]
        : []),
    ],
    prefix: [
      ...(record?.protocol === 'dual_stack' ? [record.prefixMode] : []),
      ...(observed?.protocol === 'dual_stack' && observed.prefixMode !== 'unknown'
        ? [observed.prefixMode]
        : []),
    ],
  };
  const firstAllowed = (
    addresses: readonly Ipv6AddressMode[],
    prefixes: readonly Ipv6PrefixMode[],
  ) => {
    const addressMode = addresses.find((a) => matrix.address[a].length === 0);
    const prefixMode = prefixes.find((p) => matrix.prefix[p].length === 0);
    if (!addressMode || !prefixMode) return null;
    if (addressMode === 'none' && prefixMode === 'none') return null;
    return { addressMode, prefixMode };
  };
  return (
    firstAllowed(
      [...seen.address, ...IPV6_ADDRESS_DEFAULT_ORDER],
      [...seen.prefix, ...IPV6_PREFIX_DEFAULT_ORDER],
    ) ?? firstAllowed(IPV6_ADDRESS_DEFAULT_ORDER, IPV6_PREFIX_DEFAULT_ORDER)
  );
}

/**
 * Where the Firewall step opens — the ONU's CURRENT state, resolved exactly
 * as the Security card and the as-deployed pill resolve it
 * (`device.firewall ?? omci.firewalled`):
 *
 *   cpe       what the CPE itself reports over TR-069 (`X_HW_FirewallLevel`,
 *             `Firewall.Enable`) — authoritative once a CPE is linked, and
 *             the ONLY reading for a unit the ACS sweep linked, whose
 *             `onus.firewalled` column nobody ever wrote (management-mode
 *             and authorize are its only writers)
 *   recorded  the platform's own column (the OMCI-config read's `firewalled`)
 *             — the chassis reports no firewall state in any read, so for an
 *             OMCI ONU this is all there is
 *   assumed   neither: On for a routed unit, Off for a bridged one
 *
 * The first cut read the column alone, so a TR-069 unit whose pill read
 * "Firewall on" two cards up had this step say "never recorded", Review say
 * "not readable" — false — and a bridged apply write Off to the live CPE.
 * `assumed` is true only when neither source answers, and then the Review
 * step says plainly "its current state was not readable; this apply sets it
 * to …" rather than presenting the guess as the current value: with "leave
 * as it is" gone the apply always carries the firewall on a ZTE, so the guess
 * is at least said out loud before it is written.
 */
export function defaultFirewall(input: {
  /** The CPE's own reading over TR-069, when the ONU has a linked CPE that reports one. */
  cpe?: boolean | null | undefined;
  recorded: boolean | null | undefined;
  mode: VlanMode | null;
}): { value: boolean; assumed: boolean; source: 'cpe' | 'recorded' | 'assumed' } {
  if (typeof input.cpe === 'boolean') return { value: input.cpe, assumed: false, source: 'cpe' };
  if (typeof input.recorded === 'boolean')
    return { value: input.recorded, assumed: false, source: 'recorded' };
  return { value: input.mode === 'route', assumed: true, source: 'assumed' };
}

/**
 * The firewall line an ONU MODEL cannot carry, read off the chassis's own
 * answer in an `omci-wan` result's warnings — `"firewall enable" answered:
 * %Code 63993-GPONRM : ONT return error:unknown ME.` — the way the API's
 * `unsupportedByModel` reads it. The chassis offers `firewall` for every ONU
 * on it, but the OMCI Managed Entity belongs to the unit, and an HG8546M
 * (2,165 on this fleet) has not got it; verified live behind a C300
 * (2026-08-27). The omci-wan route tolerates that answer for the optional
 * firewall line (the WAN landed, and the apply says so) but adds no sentence
 * of its own, so the task's done text names it here — and so does the bulk
 * `wan_mode` row, from the same function, so the tray and the row say one
 * sentence. Null when every line was taken, or the warnings name no such
 * answer.
 */
export function firewallModelNote(warnings: readonly string[] | null | undefined): string | null {
  const hit = (warnings ?? []).find((w) =>
    /^"firewall\s[^"]*" answered: [\s\S]*unknown ME/i.test(w),
  );
  if (!hit) return null;
  const command = /^"([^"]+)"/.exec(hit)?.[1] ?? 'firewall';
  return `This ONU model does not implement the OMCI firewall, so "${command}" was not applied — the chassis answered "unknown ME". Everything else in this write landed.`;
}
